import { useState, useEffect, FormEvent } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Modal } from "../ui/modal";
// import Badge from "../ui/badge/Badge";
import RichTextEditor from '../common/RichTextEditor';

export default function CMSPages() {
    const [pages, setPages] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);

    const [form, setForm] = useState({
        title: '',
        name: '',
        content: '',
        active: true,
    });

    useEffect(() => {
        fetchPages();
    }, []);

    const fetchPages = async () => {
        setLoading(true);
        try {
            const res = await client.getApiContentPages() as any;
            const data = res.data?.data?.data || res.data?.data || res.data || [];
            setPages(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch content pages:', err);
            setPages([]);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (page: any) => {
        setEditingId(page.id);
        setForm({
            title: page.title || '',
            name: page.name || '', // Mapping name to name as per backend schema
            content: page.content || '',
            active: (page.active !== undefined ? page.active : (page.is_published !== undefined ? page.is_published : true)),
        });
        setIsModalOpen(true);
    };

    const handleCreate = () => {
        setEditingId(null);
        setForm({ title: '', name: '', content: '', active: true });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        // Validation
        if (!form.title.trim()) {
            Swal.fire({
                title: 'Validation Error',
                text: 'Title is required',
                icon: 'warning'
            });
            return;
        }

        if (!form.name.trim()) {
            Swal.fire({
                title: 'Validation Error',
                text: 'Name is required',
                icon: 'warning'
            });
            return;
        }

        if (!form.content.trim()) {
            Swal.fire({
                title: 'Validation Error',
                text: 'Content is required',
                icon: 'warning'
            });
            return;
        }

        try {
            if (editingId) {
                const payload = {
                    id: editingId,
                    name: form.name, // Mapping name to name as required by backend
                    title: form.title,
                    content: form.content,
                    active: form.active, // Send 'active' as status
                    is_published: form.active // Send 'is_published' for backward compatibility
                };
                await (client as any).postApiContentPageUpdate({
                    postApiContentPageUpdateRequest: payload
                });
            } else {
                const payload = {
                    name: form.name, // Mapping name to name
                    title: form.title,
                    content: form.content,
                    active: form.active,
                    is_published: form.active
                };
                await (client as any).postApiContentPageCreate({
                    postApiContentPageCreateRequest: payload
                });
            }
            setIsModalOpen(false);
            setForm({ title: '', name: '', content: '', active: true });
            setEditingId(null);
            fetchPages();

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: editingId ? 'Page updated successfully' : 'Page created successfully',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });
        } catch (err: any) {
            console.error(err);
            Swal.fire({
                title: 'Error',
                text: err?.response?.data?.message || err?.message || 'Failed to save page',
                icon: 'error'
            });
        }
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (!result.isConfirmed) return;
        try {
            await client.postApiContentPageDelete({
                postApiContentPageDeleteRequest: { id: id }
            });
            fetchPages();
        } catch (err) {
            console.error('Failed to delete page:', err);
            // Fallback for SDK bugs if strict type fails
            try {
                await client.postApiContentPageDelete({ id } as any);
                fetchPages();
            } catch (retryErr) {
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to delete page',
                    icon: 'error'
                });
            }
        }
    };

    return (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Content Pages</h3>
                <div className="flex items-center gap-2">
                    {loading && <span className="text-sm text-gray-500">Loading...</span>}
                    <button
                        onClick={handleCreate}
                        className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm"
                    >
                        Create Page
                    </button>
                </div>
            </div>
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableCell isHeader className="px-5 py-3 text-start">Title</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Name</TableCell>
                            {/* <TableCell isHeader className="px-5 py-3 text-start">Status</TableCell> */}
                            <TableCell isHeader className="px-5 py-3 text-end">Actions</TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {pages.length === 0 ? (
                            <TableRow><TableCell colSpan={4} className="px-5 py-4 text-center">No pages found.</TableCell></TableRow>
                        ) : (
                            pages.map(item => (
                                <TableRow key={item.id}>
                                    <TableCell className="px-5 py-4 font-medium text-gray-900 dark:text-white">{item.title}</TableCell>
                                    <TableCell className="px-5 py-4 text-sm text-gray-500">{item.name}</TableCell>
                                    {/* <TableCell className="px-5 py-4">
                                        <Badge color={(item.active || item.is_published) ? 'success' : 'error'} size="sm">
                                            {(item.active || item.is_published) ? 'Published' : 'Draft'}
                                        </Badge>
                                    </TableCell> */}
                                    <TableCell className="px-4 py-3 text-end">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="text-blue-600 hover:underline"
                                                title="Edit Page"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item.id)}
                                                className="text-red-600 hover:underline"
                                                title="Delete Page"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-4xl" showCloseButton={false}>
                <div className="flex flex-col max-h-[90vh] bg-white dark:bg-gray-900 rounded-3xl overflow-hidden shadow-2xl">
                    {/* Fixed Header */}
                    <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-900">
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                            {editingId ? 'Edit Page' : 'Create Content Page'}
                        </h3>
                        <button
                            onClick={() => setIsModalOpen(false)}
                            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        <form id="cms-page-form" onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                        Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={form.title}
                                        onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                                        placeholder="Enter page Title"
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                        Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Enter page Name"
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                    Content <span className="text-red-500">*</span>
                                </label>
                                <RichTextEditor
                                    label=""
                                    value={form.content}
                                    onChange={(val) => setForm(prev => ({ ...prev, content: val }))}
                                    folder="pages"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Status</label>
                                <select
                                    value={form.active ? 'true' : 'false'}
                                    onChange={e => setForm(prev => ({ ...prev, active: e.target.value === 'true' }))}
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none appearance-none cursor-pointer"
                                >
                                    <option value="true">Published</option>
                                    <option value="false">Draft</option>
                                </select>
                            </div>
                        </form>
                    </div>

                    {/* Fixed Footer */}
                    <div className="px-6 py-5 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-3 bg-gray-50/50 dark:bg-gray-800/30">
                        <button
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="px-6 py-2.5 text-sm font-bold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="cms-page-form"
                            className="px-6 py-2.5 text-sm font-bold text-white bg-brand-500 border border-brand-500 rounded-xl hover:bg-brand-600 shadow-[0_4px_12px_rgba(59,130,246,0.3)] transition-all active:scale-95"
                        >
                            {editingId ? 'Update Page' : 'Create Page'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
